import AccountsIcon from '@mui/icons-material/AccountBalanceOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import LabsIcon from '@mui/icons-material/ScienceOutlined';
import MigrationIcon from '@mui/icons-material/AccountTreeOutlined';
import MigrationSetupIcon from '@mui/icons-material/DomainAddOutlined';
import OrdersIcon from '@mui/icons-material/InboxOutlined';
import OrganizationSidebar from './OrganizationSidebar';
import OrganizationsIcon from '@mui/icons-material/CorporateFareOutlined';
import PVSidebar from './PVSidebar';
import PlansIcon from '@mui/icons-material/PolicyOutlined';
import PropTypes from 'prop-types';
import ProvisionIcon from '@mui/icons-material/SatelliteOutlined';
import React from 'react';
import SecureRoute from '@components/app/routes/SecureRoute';
import SettingsIcon from '@mui/icons-material/AccountCircleOutlined';
import SidebarListItem from './SidebarListItem';
import UsersIcon from '@mui/icons-material/GroupOutlined';
import routePath, {Routes} from '@account/utils/route';
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {Link, useRouteMatch} from 'react-router-dom';
import {ORDERS_URL} from '@account/config';
import {UserAvatar} from '@apps-common/ui-components';
import {hasRoleLevel, roleLevels, roleNames} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';

const Spacer = props => <Box sx={{paddingY: 2}} {...props} />;

const ListIcon = styled(ListItemIcon)({
  marginRight: 'inherit',
});

const Name = styled(Typography)(({theme}) => ({
  fontWeight: 600,
  marginTop: theme.spacing(1),
}));

const Sidebar = ({open, onClose}) => {
  const auth = useAuth();
  const theme = useTheme();

  const {deprecateContracts, showAllAccounts, migrationWizardAccess} =
    useFlags();

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const sentinelHubRedirectView = Boolean(
    useRouteMatch({path: Routes.SENTINEL_HUB_REDIRECT})
  );

  const claims = auth.user?.claims;
  if (!claims || !claims.role_level) return null;

  const avatarUrl = hasRoleLevel(auth, [roleLevels.org_user])
    ? Routes.DASHBOARD
    : routePath(Routes.ORG_DETAIL, {orgId: claims.organization_id});

  if (sentinelHubRedirectView) return null;

  return (
    <Drawer
      variant={isSmallScreen ? 'temporary' : 'permanent'}
      anchor="left"
      open={isSmallScreen ? open : true}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'relative',
          width: theme.spacing(30),
          [theme.breakpoints.only('sm')]: {
            width: theme.spacing(7),
          },
          transition: 'width 0.15s ease',
        },
      }}
    >
      <Spacer
        display={{xs: 'none', md: 'flex'}}
        flexDirection="column"
        paddingX={2}
        paddingY={3}
      >
        <UserAvatar
          component={Link}
          to={avatarUrl}
          user={{
            first_name: claims.first_name,
            last_name: claims.last_name,
          }}
          sx={{
            textDecoration: 'none',
            width: 56,
            height: 56,
            fontSize: 56 / 2,
          }}
        />

        <Name
          variant="subtitle1"
          noWrap
          title={`${claims.first_name} ${claims.last_name}`}
          data-qe="user-name"
        >
          {`${claims.first_name} ${claims.last_name}`}
        </Name>

        <Typography
          variant="body2"
          color="textSecondary"
          data-qe="user-role-level"
        >
          {claims.role_level === roleLevels.org_admin ||
          claims.role_level === roleLevels.planet_admin
            ? `${roleNames[claims.role_level]}`
            : ''}
        </Typography>
      </Spacer>

      <List disablePadding>
        <Hidden smUp>
          <ListItemButton onClick={onClose}>
            <ListIcon>
              <CloseIcon fontSize="small" />
            </ListIcon>
            <ListItemText primary="Close" />
          </ListItemButton>
        </Hidden>

        {hasRoleLevel(auth, [roleLevels.org_user]) && (
          <SidebarListItem
            to={Routes.DASHBOARD}
            icon={<DashboardIcon fontSize="small" />}
            text="Dashboard"
          />
        )}

        {showAllAccounts && hasRoleLevel(auth, [roleLevels.planet_admin]) && (
          <SidebarListItem
            to={Routes.ACCOUNTS}
            icon={<AccountsIcon fontSize="small" />}
            text="All Accounts"
            id="accounts"
          />
        )}

        {hasRoleLevel(auth, [
          roleLevels.org_admin,
          roleLevels.planet_admin,
          roleLevels.planet_user,
        ]) && (
          <>
            <SidebarListItem
              exact
              to={Routes.ORGS}
              icon={<OrganizationsIcon fontSize="small" />}
              text="All Organizations"
              id="organizations"
              parent
            />
            <Box>
              <SecureRoute
                path={Routes.ORG_DETAIL}
                roles={[
                  roleLevels.org_admin,
                  roleLevels.planet_user,
                  roleLevels.planet_admin,
                ]}
                component={OrganizationSidebar}
              />
            </Box>
            <SidebarListItem
              to={Routes.USERS}
              icon={<UsersIcon fontSize="small" />}
              text="All Users"
              id="users"
            />
          </>
        )}

        <SidebarListItem
          to={Routes.PLANS}
          icon={<PlansIcon fontSize="small" />}
          text="All Plans"
        />

        {hasRoleLevel(auth, [roleLevels.planet_admin]) &&
          migrationWizardAccess && (
            <>
              <SidebarListItem
                to={Routes.MIGRATION_EVAL_SETUP}
                icon={<MigrationSetupIcon fontSize="small" />}
                text="Migration Setup"
                id="migration-eval-setup"
              />
              <SidebarListItem
                to={Routes.MIGRATION_WIZARD}
                icon={<MigrationIcon fontSize="small" />}
                text="Migration Wizard"
                id="migration-wizard"
              />
            </>
          )}

        <Spacer>
          <Divider variant="middle" />
        </Spacer>

        {deprecateContracts &&
          hasRoleLevel(auth, [roleLevels.planet_admin]) && (
            <SidebarListItem
              to={Routes.PROVISION}
              icon={<ProvisionIcon fontSize="small" />}
              text="Provision"
            />
          )}

        {!deprecateContracts &&
          hasRoleLevel(auth, [roleLevels.planet_admin]) && (
            <>
              <SidebarListItem
                to={Routes.CONTRACTS}
                icon={<ProvisionIcon fontSize="small" />}
                text="Provision"
                id="planetary-variables"
              />
              <Box>
                <SecureRoute
                  path={Routes.CONTRACTS}
                  roles={[roleLevels.planet_admin]}
                  component={PVSidebar}
                />
              </Box>
            </>
          )}

        <SidebarListItem
          href={ORDERS_URL}
          component="a"
          icon={<OrdersIcon fontSize="small" />}
          text="My Orders"
          id="myOrders"
        />

        <SidebarListItem
          to={Routes.USER_SETTINGS}
          icon={<SettingsIcon fontSize="small" />}
          text="My Settings"
          id="mySettings"
        />

        {hasRoleLevel(auth, [
          roleLevels.planet_user,
          roleLevels.planet_admin,
        ]) && (
          <SidebarListItem
            to={Routes.LABS}
            icon={<LabsIcon fontSize="small" />}
            text="Labs"
            id="labs"
          />
        )}
      </List>
    </Drawer>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Sidebar;
